/**
 * Formatador de variáveis com sintaxe ||formato
 * Centraliza toda lógica de formatação de variáveis do sistema
 * 
 * Sintaxe: {{variavel||formato}}
 * 
 * Formatos suportados:
 * - Data: dd/MM/yyyy, dd/MM/yyyy HH:mm, dd 'de' MMMM 'de' yyyy
 * - Moeda: moeda (R$ 1.234,56)
 * - Número: numero (1.234,56)
 * - Documentos: cpf, cnpj
 * - Telefone: telefone
 * - Texto: maiusculo, minusculo, capitalizar
 * 
 * Versão: 1.0.0
 */

import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { applyCPFMask, applyCNPJMask, applyPhoneMask } from '@/lib/input-masks';
import { formatCurrency } from '@/lib/currency-utils';

/**
 * Nomes dos meses em pt-BR para formatos de data customizados
 */
const MONTH_NAMES_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

/**
 * Separa expressão de variável em key e formato opcional
 * Ex: "data_vencimento||dd/MM/yyyy" → { key: "data_vencimento", format: "dd/MM/yyyy" }
 * Ex: "nome_cliente" → { key: "nome_cliente", format: undefined }
 * 
 * @param expression - Expressão da variável (sem as chaves {{ }})
 * @returns Objeto com key e format opcional
 */
export function parseVariableWithFormat(expression: string): { key: string; format?: string } {
  const separatorIndex = expression.indexOf('||');
  if (separatorIndex === -1) {
    return { key: expression };
  }
  return {
    key: expression.substring(0, separatorIndex),
    format: expression.substring(separatorIndex + 2),
  };
}

/**
 * Tenta parsear um valor como data ISO
 * Suporta: "2026-03-27", "2026-03-27T14:30:00", "2026-03-27T14:30:00.000Z"
 * 
 * @param value - Valor a ser parseado
 * @returns Date válida ou null
 */
function tryParseDate(value: any): Date | null {
  if (!value) return null;
  const str = String(value);
  
  // Verifica se parece ser uma data ISO
  if (!/^\d{4}-\d{2}-\d{2}/.test(str)) return null;
  
  try {
    const parsed = parseISO(str);
    if (isValid(parsed)) return parsed;
  } catch {
    // Não é uma data válida
  }
  return null;
}

/**
 * Verifica se o formato é um padrão de data (contém tokens como dd, MM, yyyy, HH, mm)
 * 
 * @param fmt - String de formato
 * @returns true se parece ser um formato de data
 */
function isDateFormat(fmt: string): boolean {
  return /[dMyHhms]/.test(fmt);
}

/**
 * Aplica formatação a um valor baseado no formato especificado
 * 
 * @param value - Valor bruto da variável (string, número, etc.)
 * @param fmt - Formato a ser aplicado (ex: "dd/MM/yyyy", "moeda", "cpf")
 * @returns Valor formatado como string
 */
export function applyVariableFormat(value: any, fmt: string): string {
  if (value === null || value === undefined) return '';
  
  const trimmedFormat = fmt.trim().toLowerCase();
  
  // === Formatos nomeados (atalhos) ===
  
  // Moeda BRL
  if (trimmedFormat === 'moeda') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return String(value);
    return formatCurrency(num);
  }
  
  // Número formatado pt-BR
  if (trimmedFormat === 'numero') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return String(value);
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }
  
  // CPF
  if (trimmedFormat === 'cpf') {
    return applyCPFMask(String(value));
  }
  
  // CNPJ
  if (trimmedFormat === 'cnpj') {
    return applyCNPJMask(String(value));
  }
  
  // Telefone
  if (trimmedFormat === 'telefone') {
    return applyPhoneMask(String(value));
  }
  
  // Maiúsculas
  if (trimmedFormat === 'maiusculo') {
    return String(value).toUpperCase();
  }
  
  // Minúsculas
  if (trimmedFormat === 'minusculo') {
    return String(value).toLowerCase();
  }
  
  // Capitalizar (primeira letra de cada palavra em maiúscula)
  if (trimmedFormat === 'capitalizar') {
    return String(value)
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
  }
  
  // === Formatos de data (padrões date-fns) ===
  if (isDateFormat(fmt)) {
    const dateValue = tryParseDate(value);
    if (dateValue) {
      try {
        return format(dateValue, fmt, { locale: ptBR });
      } catch {
        // Formato inválido, retorna valor original
        return String(value);
      }
    }
    // Valor não é data, retorna como está
    return String(value);
  }
  
  // Formato não reconhecido — retorna valor original
  return String(value);
}

/**
 * Regex global para capturar variáveis com formato opcional
 * Captura: {{variavel}} ou {{variavel||formato}}
 * Grupo 1: nome da variável (suporta paths aninhados com pontos)
 * Grupo 2: formato opcional (tudo após ||)
 * 
 * IMPORTANTE: Usar .replace() com esta regex para processar templates
 */
export const VARIABLE_WITH_FORMAT_REGEX = /\{\{(\w+(?:\.\w+)*)(?:\|\|([^}]+))?\}\}/g;
